import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError, markAuthConfirmed, markAuthCleared } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    // Prompt 18 A3 — /qbo/disconnected must work logged-out and make
    // zero API calls. Short-circuit AuthProvider's /auth/me probe when
    // the browser is sitting on that exact page. Legal routes still
    // fire /auth/me so the top-bar reflects session state if present.
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (path === "/qbo/disconnected") {
      markAuthCleared();
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      markAuthConfirmed();
    } catch {
      // 401 or network error — treat as unauthenticated.
      // Do NOT flip auth-confirmed here; the 401 interceptor only redirects
      // to /login?session=expired when a previously confirmed session dies.
      markAuthCleared();
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Verifies the server-set session cookie with /auth/me before flipping user state.
  const _completeAuth = useCallback(async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      markAuthConfirmed();
      return { ok: true };
    } catch (e) {
      // Cookie rejected by the browser — surface a real error.
      markAuthCleared();
      setUser(false);
      return { ok: false, error: "Couldn't establish a session. Please try again." };
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      return await _completeAuth(data);
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, [_completeAuth]);

  const register = useCallback(async (email, password, name, companyName) => {
    try {
      const { data } = await api.post("/auth/register", {
        email, password, name, company_name: companyName,
      });
      return await _completeAuth(data);
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, [_completeAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Even if the logout endpoint fails, we clear local state so the user
      // is signed out client-side. Log for debugging but don't block.
      console.warn("Logout endpoint failed (clearing local state anyway):", e?.message);
    }
    markAuthCleared();
    setUser(false);
  }, []);

  // Stable object identity — prevents needless re-renders of every consumer.
  const value = useMemo(
    () => ({ user, login, register, logout, refresh }),
    [user, login, register, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
